import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { type CategoryTree } from '@/shared/category/type';
import { type BuildTranslationKeys } from '@/types/translation';
import { isAllGrades } from '@/views/build/util';

/**
 * 학년 옵션 인터페이스
 */
export type GradeOption = {
  label: string;
  ids: string[];
};

const GRADE_OPTIONS_LABEL_TRANSLATION_KEY = [
  'build.standard.grade.elem_12',
  'build.standard.grade.elem_34',
  'build.standard.grade.elem_56',
  'build.standard.grade.middle',
  'build.standard.grade.high',
] as BuildTranslationKeys[];

/**
 * 학교급별 ID 상수
 */
const SCHOOL_TYPE_ID = {
  ELEMENTARY: '101', // 초등학교
  MIDDLE: '102', // 중학교
  HIGH: '103', // 고등학교
} as const;

/**
 * 학년 옵션 생성 hook with i18n labels
 * @param schoolInfoTree - 학교 정보 트리
 * @returns GradeOption[] with translated labels
 */
export const useGradeOptions = (
  schoolInfoTree?: CategoryTree,
): GradeOption[] => {
  const { t } = useTranslation(['build']);

  const getGradeOptions = useCallback((schoolInfoTree: CategoryTree) => {
    const gradeOptions: GradeOption[] = [];

    // Iterate through each school type (초등/중등/고등)
    for (const schoolType of schoolInfoTree.children || []) {
      const allChildren = schoolType.children || [];
      const schoolTypeId = schoolType.categoryTreeId;

      // 전학년(categoryGroupId: 99) 노드를 chunking에서 분리
      const regularGrades = allChildren.filter(
        grade => !isAllGrades(grade.categoryGroupId),
      );
      const allGradesNode = allChildren.find(grade =>
        isAllGrades(grade.categoryGroupId),
      );

      // Determine chunk size based on school type
      let chunkSize: number;
      if (schoolTypeId === SCHOOL_TYPE_ID.ELEMENTARY) {
        chunkSize = 2; // 초등: 1-2, 3-4, 5-6
      } else if (
        schoolTypeId === SCHOOL_TYPE_ID.MIDDLE ||
        schoolTypeId === SCHOOL_TYPE_ID.HIGH
      ) {
        chunkSize = 3; // 중등/고등: 1-2-3
      } else {
        continue; // Unknown school type, skip
      }

      // Chunk regular grades into groups (전학년 제외)
      for (let i = 0; i < regularGrades.length; i += chunkSize) {
        const ids = regularGrades
          .slice(i, i + chunkSize)
          .map(grade => grade.categoryTreeId);

        // 전학년 ID를 각 chunk에 추가 (전학년 과목도 검색에 포함)
        if (allGradesNode) {
          ids.push(allGradesNode.categoryTreeId);
        }

        if (ids.length > 0) {
          gradeOptions.push({ label: '', ids });
        }
      }
    }

    return gradeOptions;
  }, []);

  return useMemo(() => {
    if (!schoolInfoTree) return [];

    // Generate grade groups (ids only)
    const gradeGroups = getGradeOptions(schoolInfoTree);

    // Add translated labels
    return [
      { label: t('build.standard.grade.all'), ids: [] },
      ...gradeGroups.map((group, index) => ({
        label: t(GRADE_OPTIONS_LABEL_TRANSLATION_KEY[index]),
        ids: group.ids,
      })),
    ];
  }, [schoolInfoTree, t, getGradeOptions]);
};
